import React, { useState, useMemo } from 'react';
import { X } from './Icons';
import { roleplayData } from '../data/roleplayData';
import { useSupabaseData } from '../hooks/useSupabaseData';

const RoleplayPage = ({ onBack, onScenarioClick, onLabOptionClick, userPreferences }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('roleplay');

  const { displayLanguage } = userPreferences;

  // Load roleplay sections from Supabase with fallback
  const { data: remote } = useSupabaseData({
    table: 'eng_roleplay',
    mapRow: (row) => row,
    getFallback: () => null
  });

  const data = useMemo(() => {
    console.log('RoleplayPage - remote data:', remote);
    
    if (!remote || (Array.isArray(remote) && remote.length === 0)) {
      console.log('RoleplayPage - Using fallback data');
      return roleplayData;
    }
    
    const rows = Array.isArray(remote) ? remote : [remote];
    const out = { overview: roleplayData.overview };

    // Search section
    const search = rows.find(r => r.data_type === 'search_section');
    console.log('RoleplayPage - search section:', search);
    out.searchSection = search ? {
      placeholder: {
        tc: search.search_placeholder_tc || roleplayData.searchSection.placeholder.tc,
        sc: search.search_placeholder_sc || roleplayData.searchSection.placeholder.sc,
        en: search.search_placeholder_en || roleplayData.searchSection.placeholder.en
      },
      cameraIcon: search.search_camera_icon || '📷',
      microphoneIcon: search.search_microphone_icon || '🎤'
    } : roleplayData.searchSection;

    // Lab options - get the lab title and options
    const labTitle = rows.find(r => r.data_type === 'roleplay_option' && r.roleplay_lab_title_en);
    out.roleplayLab = { 
      title: labTitle ? {
        tc: labTitle.roleplay_lab_title_tc,
        sc: labTitle.roleplay_lab_title_sc,
        en: labTitle.roleplay_lab_title_en
      } : roleplayData.roleplayLab.title, 
      options: [] 
    };
    
    rows.filter(r => r.data_type === 'roleplay_option' && r.option_id).forEach(r => {
      out.roleplayLab.options.push({
        id: r.option_id,
        icon: r.option_icon || '🧪',
        gradient: r.option_gradient || 'from-purple-500 to-pink-500',
        title: {
          tc: r.option_title_tc || '',
          sc: r.option_title_sc || '',
          en: r.option_title_en || ''
        }
      });
    });

    // Only for you scenarios
    out.onlyForYou = { title: roleplayData.onlyForYou.title, scenarios: [] };
    rows.filter(r => r.data_type === 'scenario' && r.scenario_section === 'only_for_you').forEach(r => {
      out.onlyForYou.scenarios.push({
        id: r.scenario_id,
        icon: r.scenario_icon || '🎯',
        difficultyColor: r.scenario_difficulty_color || 'blue',
        difficulty: { 
          tc: r.scenario_difficulty_tc || '中級', 
          sc: r.scenario_difficulty_sc || '中级', 
          en: r.scenario_difficulty_en || 'Intermediate' 
        },
        duration: { 
          tc: r.scenario_duration_tc || '10 分鐘', 
          sc: r.scenario_duration_sc || '10 分钟', 
          en: r.scenario_duration_en || '10 min' 
        },
        title: { 
          tc: r.scenario_title_tc || '', 
          sc: r.scenario_title_sc || '', 
          en: r.scenario_title_en || '' 
        }
      });
    });

    // Trending scenarios
    out.trendingNow = { title: roleplayData.trendingNow.title, scenarios: [] };
    rows.filter(r => r.data_type === 'scenario' && r.scenario_section === 'trending_now').forEach(r => {
      out.trendingNow.scenarios.push({
        id: r.scenario_id,
        icon: r.scenario_icon || '🔥',
        difficultyColor: r.scenario_difficulty_color || 'blue',
        difficulty: { 
          tc: r.scenario_difficulty_tc || '中級', 
          sc: r.scenario_difficulty_sc || '中级', 
          en: r.scenario_difficulty_en || 'Intermediate' 
        },
        duration: { 
          tc: r.scenario_duration_tc || '10 分鐘', 
          sc: r.scenario_duration_sc || '10 分钟', 
          en: r.scenario_duration_en || '10 min' 
        },
        title: { 
          tc: r.scenario_title_tc || '', 
          sc: r.scenario_title_sc || '', 
          en: r.scenario_title_en || '' 
        }
      });
    });

    console.log('RoleplayPage - processed data:', out);

    // Bottom navigation
    out.bottomNavigation = roleplayData.bottomNavigation;
    return out;
  }, [remote]);

  const getText = (textObj) => {
    return textObj[displayLanguage] || textObj.en;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    console.log('Searching for:', searchQuery);
  };

  const handleScenarioClick = (scenarioId) => {
    onScenarioClick(scenarioId);
  };

  const handleLabOptionClick = (optionId) => {
    onLabOptionClick(optionId);
  };

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
  };

  const getDifficultyColor = (color) => {
    const colors = {
      orange: 'bg-orange-500',
      green: 'bg-green-500',
      blue: 'bg-blue-500'
    };
    return colors[color] || 'bg-gray-500';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      {/* Header */}
      <div className="flex items-center justify-between p-6">
        <h1 className="text-2xl font-bold">{getText(data.overview.title)}</h1>
        <button 
          onClick={onBack}
          className="flex items-center text-white hover:text-blue-300 transition-colors"
        >
          <X size={24} />
        </button>
      </div>

      {/* Search Bar */}
      <div className="px-6 mb-6">
        <form onSubmit={handleSearch} className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={getText(data.searchSection.placeholder)}
            className="w-full bg-gray-800/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
            <button type="button" className="text-gray-400 hover:text-white">
              <span className="text-lg">{data.searchSection.cameraIcon}</span>
            </button>
            <button type="button" className="text-gray-400 hover:text-white">
              <span className="text-lg">{data.searchSection.microphoneIcon}</span>
            </button>
          </div>
        </form>
      </div>

      {/* Role-Play Lab Section */}
      <div className="px-6 mb-8">
        <h2 className="text-center text-lg font-bold mb-4">
          {getText(data.roleplayLab.title)}
        </h2>
        <div className="grid grid-cols-2 gap-4">
          {data.roleplayLab.options.map((option) => (
            <button
              key={option.id}
              onClick={() => handleLabOptionClick(option.id)}
              className={`bg-gradient-to-r ${option.gradient} rounded-xl p-6 text-center hover:scale-105 transition-transform`}
            >
              <div className="text-4xl mb-3">{option.icon}</div>
              <h3 className="font-semibold text-white">
                {getText(option.title)}
              </h3>
            </button>
          ))}
        </div>
      </div>

      {/* Only for you Section */}
      <div className="px-6 mb-8">
        <h2 className="text-lg font-bold mb-4">
          {getText(data.onlyForYou.title)}
        </h2>
        <div className="flex space-x-4 overflow-x-auto pb-4">
          {data.onlyForYou.scenarios.map((scenario) => (
            <div
              key={scenario.id}
              onClick={() => handleScenarioClick(scenario.id)}
              className="bg-gray-800/50 rounded-xl p-4 min-w-[200px] cursor-pointer hover:bg-gray-700/50 transition-colors"
            >
              <div className="relative">
                <div className={`absolute top-0 left-0 px-2 py-1 rounded text-xs font-semibold ${getDifficultyColor(scenario.difficultyColor)}`}>
                  {getText(scenario.difficulty)}
                </div>
                <div className="absolute bottom-0 left-0 px-2 py-1 rounded text-xs bg-gray-700/80">
                  {getText(scenario.duration)}
                </div>
                <div className="text-3xl mb-3 text-center pt-6">{scenario.icon}</div>
              </div>
              <h3 className="font-semibold text-center">
                {getText(scenario.title)}
              </h3>
            </div>
          ))}
        </div>
      </div>

      {/* Trending now Section */}
      <div className="px-6 mb-20">
        <h2 className="text-lg font-bold mb-4">
          {getText(data.trendingNow.title)}
        </h2>
        <div className="flex space-x-4 overflow-x-auto pb-4">
          {data.trendingNow.scenarios.map((scenario) => (
            <div
              key={scenario.id}
              onClick={() => handleScenarioClick(scenario.id)}
              className="bg-gray-800/50 rounded-xl p-4 min-w-[200px] cursor-pointer hover:bg-gray-700/50 transition-colors"
            >
              <div className="relative">
                <div className={`absolute top-0 left-0 px-2 py-1 rounded text-xs font-semibold ${getDifficultyColor(scenario.difficultyColor)}`}>
                  {getText(scenario.difficulty)}
                </div>
                <div className="absolute bottom-0 left-0 px-2 py-1 rounded text-xs bg-gray-700/80">
                  {getText(scenario.duration)}
                </div>
                <div className="text-3xl mb-3 text-center pt-6">{scenario.icon}</div>
              </div>
              <h3 className="font-semibold text-center">
                {getText(scenario.title)}
              </h3>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-900/90 backdrop-blur-sm border-t border-gray-700">
        <div className="flex justify-around p-4">
          {data.bottomNavigation.tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              className={`px-4 py-2 rounded-full transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {getText(tab.title)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RoleplayPage;
